/**
 * FeedbackModal Component
 * Modal for submitting user feedback with categorization
 */

import React, { useState } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/Select';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import {
  submitFeedback,
  retrySubmitFeedback,
  validateFeedbackData,
  getFriendlyErrorMessage,
  FeedbackApiError,
  type FeedbackSubmissionData,
  type ValidationError
} from '../../utils/feedbackApi';
import { ImageUploadField } from './ImageUploadField';

export type FeedbackCategory = 'issue' | 'idea' | 'question' | 'complaint' | 'feature-request' | 'other';

interface FeedbackFormData {
  title: string;
  category: FeedbackCategory | '';
  message: string;
  imageUrls: string[];
}

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_CATEGORIES = [
  { value: 'issue', label: 'Issue' },
  { value: 'idea', label: 'Idea' },
  { value: 'question', label: 'Question' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'feature-request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
] as const;

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, getAccessToken } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState<FeedbackFormData>({
    title: '',
    category: '',
    message: '',
    imageUrls: [],
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Character counts for validation feedback
  const titleLength = formData.title.length;
  const messageLength = formData.message.length;

  const validateForm = (): boolean => {
    const validationErrors = validateFeedbackData({
      title: formData.title,
      category: formData.category as FeedbackSubmissionData['category'],
      message: formData.message,
    });

    const newErrors: Record<string, string> = {};
    validationErrors.forEach((error: ValidationError) => {
      newErrors[error.field] = error.message;
    });

    setErrors(newErrors);
    return validationErrors.length === 0;
  };

  const handleSubmit = async (useRetry: boolean = false) => {
    if (loading) return;

    if (!validateForm()) {
      showToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fix the errors below and try again.',
      });
      return;
    }

    setLoading(true);

    try {
      const feedbackData: FeedbackSubmissionData = {
        title: formData.title.trim(),
        category: formData.category as FeedbackSubmissionData['category'],
        message: formData.message.trim(),
        imageUrls: formData.imageUrls,
      };

      let result;
      if (useRetry && retryCount < 3) {
        // Use retry logic for failed submissions
        result = await retrySubmitFeedback(feedbackData, getAccessToken, 3, 1000);
        setRetryCount(prev => prev + 1);
      } else {
        // Normal submission
        result = await submitFeedback(feedbackData, getAccessToken);
        setRetryCount(0);
      }

      if (result.success) {
        // Show success message
        showToast({
          type: 'success',
          title: '🎉 Feedback Submitted!',
          message: result.message || 'Thank you for your feedback! 🙏 We really appreciate you taking the time to help us improve.',
        });

        // Reset form and close modal
        handleClose();
      } else {
        // Handle API error response
        showToast({
          type: 'error',
          title: 'Submission Failed',
          message: result.error?.message || 'Failed to submit feedback. Please try again.',
        });
      }
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      let errorMessage = 'Failed to submit feedback. Please try again.';
      let showRetryOption = false;
      
      if (error instanceof FeedbackApiError) {
        errorMessage = getFriendlyErrorMessage(error);
        
        // Show retry option for system errors but not validation errors
        showRetryOption = error.type === 'SYSTEM_ERROR' || error.type === 'EMAIL_ERROR';
      }
      
      showToast({
        type: 'error',
        title: 'Submission Failed',
        message: showRetryOption && retryCount < 3 
          ? `${errorMessage} Click the submit button to retry.`
          : errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;

    // Reset form
    setFormData({
      title: '',
      category: '',
      message: '',
      imageUrls: [],
    });
    setErrors({});
    setRetryCount(0);
    onClose();
  };

  const handleInputChange = (field: keyof FeedbackFormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Give Feedback
          </DialogTitle>
          <DialogDescription>
            Help us improve the estimation tool by sharing your thoughts, ideas, or reporting issues.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title Field */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="feedback-title">
                Title *
              </Label>
              <span className={`text-xs ${titleLength > 200 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {titleLength}/200
              </span>
            </div>
            <Input
              id="feedback-title"
              type="text"
              placeholder="Brief summary of your feedback"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={loading}
              className={errors.title ? 'border-destructive' : ''}
              maxLength={250} // Allow a bit over the limit for better UX
            />
            {errors.title && (
              <p className="text-xs text-destructive">{errors.title}</p>
            )}
          </div>

          {/* Category Field */}
          <div className="space-y-2">
            <Label htmlFor="feedback-category">
              Category *
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => handleInputChange('category', value)}
              disabled={loading}
            >
              <SelectTrigger className={errors.category ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_CATEGORIES.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-xs text-destructive">{errors.category}</p>
            )}
          </div>

          {/* Message Field */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="feedback-message">
                Message *
              </Label>
              <span className={`text-xs ${messageLength > 2000 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {messageLength}/2000
              </span>
            </div>
            <Textarea
              id="feedback-message"
              placeholder="Please provide detailed information about your feedback..."
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              disabled={loading}
              rows={4}
              className={errors.message ? 'border-destructive' : ''}
              maxLength={2100} // Allow a bit over the limit for better UX
            />
            {errors.message && (
              <p className="text-xs text-destructive">{errors.message}</p>
            )}
          </div>

          {/* Image Upload Field */}
          <ImageUploadField
            value={formData.imageUrls}
            onChange={(urls) => handleInputChange('imageUrls', urls)}
            disabled={loading}
          />

          {/* User Context Info */}
          {user && (
            <div className="p-3 bg-muted/50 rounded-md border">
              <p className="text-xs text-muted-foreground mb-1">
                This feedback will be sent from:
              </p>
              <p className="text-sm font-medium">
                {user.name} ({user.email})
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit(false)}
            disabled={loading || !formData.title.trim() || !formData.category || !formData.message.trim()}
            isLoading={loading}
          >
            {loading ? (
              retryCount > 0 ? `Retrying... (${retryCount}/3)` : 'Submitting...'
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {retryCount > 0 ? 'Retry Submission' : 'Submit Feedback'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};